import { useEffect, useState } from "react";

import { billingRepository } from "@/application/billing/billing_repository";
import type { BillingAccountRow } from "@/application/billing/types";
import { ProviderPill } from "@/components/ui/ProviderPill";
import { toPillProvider } from "@/components/ui/provider-pill-providers";

import { SidebarField, SidebarSection } from "./SidebarSection";
import { StageBadge } from "./StageBadge";

/**
 * The three things about a merchant's billing worth a sidebar slot: what
 * state it is in, which rail it is on, and whether they are entitled.
 *
 * Reads the account row directly rather than through `BillingPanelBloc` —
 * the panel also pages the event log, which this does not show.
 */
export function BillingCard({
  businessId,
  onViewAll,
}: {
  businessId: string;
  onViewAll: () => void;
}) {
  const [account, setAccount] = useState<BillingAccountRow | null>(null);
  const [status, setStatus] = useState<"loading" | "loaded" | "error">(
    "loading",
  );

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");

    billingRepository
      .getAccount(businessId)
      .then((row) => {
        if (cancelled) return;
        setAccount(row);
        setStatus("loaded");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [businessId]);

  const pill = account?.provider ? toPillProvider(account.provider) : null;

  return (
    <SidebarSection title="Billing" onViewAll={onViewAll}>
      {status === "loading" && (
        <p className="text-subtle-foreground text-sm">Loading…</p>
      )}

      {status === "error" && (
        <p className="text-sm text-amber-700">Could not load billing.</p>
      )}

      {status === "loaded" && !account && (
        <p className="text-muted-foreground text-sm">No billing record.</p>
      )}

      {status === "loaded" && account && (
        <>
          {account.state && (
            <SidebarField label="State">
              <StageBadge stage={account.state} />
            </SidebarField>
          )}
          <SidebarField label="Plan">
            {account.plan ?? <span className="text-subtle-foreground">—</span>}
          </SidebarField>
          <SidebarField label="Provider">
            {pill ? (
              <ProviderPill provider={pill} />
            ) : (
              (account.provider ?? (
                <span className="text-subtle-foreground">—</span>
              ))
            )}
          </SidebarField>
          <SidebarField label="Entitled">
            <span
              className={
                account.active
                  ? "font-medium text-emerald-700"
                  : "text-muted-foreground font-medium"
              }
            >
              {account.active ? "Yes" : "No"}
            </span>
          </SidebarField>
          {/* Only an explicit `false` is a cancellation. Unknown is not. */}
          {account.autoRenew === false && (
            <SidebarField label="Renewal">
              <span className="font-medium text-amber-700">Not renewing</span>
            </SidebarField>
          )}
        </>
      )}
    </SidebarSection>
  );
}
